// Small labels (mockup ui.badge / ui.status / ui.connStatus / ui.roleBadge / ui.sourceBadge / ui.levelBadge /
// ui.redacted / ui.text). State is always icon/dot + text, never colour alone (plan 8.7).
import type { ReactNode } from 'react';
import { MARKER_SOURCE, markerKind } from '@aiax-hub/redact/markers';
import { useHasT, useT } from '../i18n';
import { cls } from '../lib/util';
import { Icon } from './Icon';
import { sourceInfo } from './sources';

export type BadgeTone = 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info';
export function Badge({ tone = 'neutral', children, className }: { tone?: BadgeTone; children: ReactNode; className?: string }) {
  return <span className={cls('badge', `badge--${tone}`, className)}>{children}</span>;
}

const STATUS_MAP: Record<string, string> = {
  ok: 'ok',
  connected: 'ok',
  quiet: 'warn',
  down: 'danger',
  warn: 'warn',
  danger: 'danger',
  info: 'info',
  idle: 'idle',
  active: 'ok',
  grace: 'warn',
  revoked: 'idle',
  expired: 'danger',
  pending: 'info',
  denied: 'danger',
  success: 'ok',
  failed: 'danger',
};
/** Dot + text pill: kind ok|warn|danger|info|idle or a connection/key state (connected, quiet, down, revoked …). */
export function Status({ kind, children }: { kind: string; children?: ReactNode }) {
  const t = useT();
  return <span className={`status-pill status-pill--${STATUS_MAP[kind] ?? 'idle'}`}>{children ?? t('status.' + kind)}</span>;
}

/** Connection state (ok = connected · quiet · down) + "error" badge when the server flagged a problem. */
export function ConnStatus({ status, problem, reason }: { status: 'ok' | 'quiet' | 'down' | string; problem?: boolean; reason?: string }) {
  const t = useT();
  return (
    <span className="conn-status" title={reason}>
      <Status kind={status === 'ok' ? 'connected' : status}>{t('conn.' + status)}</Status>
      {problem ? (
        <span className="badge badge--danger">
          <Icon name="alert" />
          {t('conn.problem')}
        </span>
      ) : null}
    </span>
  );
}

const ROLE_TONE: Record<string, string> = {
  admin: 'admin',
  manager: 'manager',
  member: 'user',
  content_reviewer: 'viewer',
  log_reviewer: 'reviewer',
  ops: 'ops',
  stats_viewer: 'stats',
  delegated_admin: 'admin',
  custom_teamlead: 'manager',
};
/** Role badge (plan 8.1.2 colours). Unknown/custom roles show `name` when given. */
export function RoleBadge({ role, name }: { role: string; name?: string }) {
  const t = useT();
  const hasT = useHasT();
  const id = role === 'auditor' ? 'content_reviewer' : role;
  return <span className={`role-badge role-badge--${ROLE_TONE[id] ?? 'user'}`}>{name ?? (hasT('role.' + id) ? t('role.' + id) : id)}</span>;
}

/** Usage source badge: tool colour + icon (terminal = CLI tool, key = API key call) + usage method. */
export function SourceBadge({ source, compact }: { source: string; compact?: boolean }) {
  const t = useT();
  const info = sourceInfo(source);
  return (
    <span
      className={cls('src-badge', `src-badge--${info.path}`, compact && 'src-badge--compact')}
      style={{ ['--c' as string]: info.color }}
      title={`${info.label} · ${t('path.' + info.path)}`}
    >
      <Icon name={info.icon} className="src-badge__ic" />
      <span className="src-badge__name">{info.label}</span>
      {compact ? null : <span className="src-badge__path">{t('path.' + info.path)}</span>}
    </span>
  );
}

/** Record level ①~④ with dots; compact shows only the number (name in tooltip / screen-reader text). */
export function LevelBadge({ level, compact }: { level: 1 | 2 | 3 | 4; compact?: boolean }) {
  const t = useT();
  const full = t('level.short.' + level);
  const num = t('level.' + level).split(' ')[0];
  return (
    <span className={cls('level-badge', `level-badge--${level}`, compact && 'level-badge--compact')} title={`${t('level.' + level)} — ${t('level.desc.' + level)}`}>
      <span className="level-badge__dots" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
          <i key={i} className={i <= level ? 'is-on' : undefined} />
        ))}
      </span>
      {compact ? (
        <>
          <span aria-hidden="true">{num}</span>
          <span className="visually-hidden">{full}</span>
        </>
      ) : (
        <span>{full}</span>
      )}
    </span>
  );
}

/** Masked part of a text: ▇▇▇▇ + "가림: 이메일". */
export function Redacted({ kind, marker }: { kind: string; marker?: string }) {
  const t = useT();
  const hasT = useHasT();
  return (
    <span className="redacted" title={marker}>
      <span className="redacted__bar" aria-hidden="true">
        ▇▇▇▇
      </span>
      <small>{t('redact.label', { kind: hasT('redact.' + kind) ? t('redact.' + kind) : kind })}</small>
    </span>
  );
}

/**
 * People's own text with canonical redaction markers (and legacy {{redact:kind}}) drawn as Redacted.
 * Marked .rich: excluded from the wording check because nobody at METIS Hub wrote it.
 */
export function RichText({ text }: { text: string | null | undefined }) {
  if (text == null) return <span className="rich" />;
  return (
    <span className="rich">
      {String(text)
        .split(new RegExp(`(${MARKER_SOURCE}|\\{\\{redact:[a-z_]+\\}\\})`))
        .map((part, i) => {
          const m = /^\{\{redact:([a-z_]+)\}\}$/.exec(part);
          const kind = m?.[1] ?? markerKind(part);
          return kind ? <Redacted key={i} kind={kind} marker={part} /> : part;
        })}
    </span>
  );
}

/**
 * Person avatar: the first letter of the name, or "#" when the name shown is a pseudonym (no real-name permission).
 * People avatars never use tool colours (plan 10.5).
 */
export function Avatar({ name, anonymous, size }: { name: string; anonymous?: boolean; size?: 'sm' | 'lg' }) {
  return (
    <span className={cls('avatar', size === 'sm' && 'avatar--sm', size === 'lg' && 'avatar--lg')} aria-hidden="true">
      {anonymous ? '#' : (name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}
